/**
 * Pill Button Component TypeScript
 * Handles button interactions and accessibility features
 */

// Self-invoking function to avoid global scope pollution
  // Define the pill button component functionality
  class PillButtonComponent {
    private element: HTMLElement;

    constructor(element: HTMLElement) {
      this.element = element;
      this.init();
    }
    
    private init(): void {
      // Add event listeners
      this.addEventListeners();
      
      // Set focus handling for the focus ring effect
      this.setupFocusHandling();
    }
    
    private addEventListeners(): void {
      // Example: track analytics on button click
      this.element.addEventListener('click', (e: MouseEvent) => {
        // Prevent double submissions for forms
        if (this.element.getAttribute('type') === 'submit') {
          if (this.element.classList.contains('processing')) {
            e.preventDefault();
            return false as any;
          }
          this.element.classList.add('processing');
        }
        
        // Custom event for analytics tracking
        const analyticsEvent = new CustomEvent('button:click', {
          bubbles: true,
          detail: {
            buttonText: this.element.querySelector('.pill-button__label')?.textContent,
            buttonType: this.element.getAttribute('type'),
            buttonVariant: this.getButtonVariant(),
            buttonContext: this.element.classList.contains('dark-bg') ? 'dark' : 'light'
          }
        });
        
        this.element.dispatchEvent(analyticsEvent);
      });
    }
    
    private getButtonVariant(): string {
      const variants = ['primary', 'secondary', 'text', 'success', 'error'];
      return variants.find(variant => this.element.classList.contains(variant)) || 'primary';
    }
    
    private setupFocusHandling(): void {
      // Add class for keyboard focus only (not mouse click)
      this.element.addEventListener('mousedown', () => {
        this.element.classList.add('mouse-focus');
      });
      
      this.element.addEventListener('blur', () => {
        this.element.classList.remove('mouse-focus');
        this.element.classList.remove('keyboard-focus');
      });
      
      this.element.addEventListener('focus', () => {
        if (!this.element.classList.contains('mouse-focus')) {
          this.element.classList.add('keyboard-focus');
        }
      });
      
      this.element.addEventListener('mouseup', () => {
        this.element.classList.remove('mouse-focus');
      });
    }
  }
  
  // Initialize all pill buttons when the DOM is fully loaded
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.pill-button').forEach(button => {
      new PillButtonComponent(button as HTMLElement);
    });
  });